import type { FieldDef, SimpleMasterPageConfig } from '../components/SimpleMasterPage';
import { BUSINESS_TYPES, REG_SECTIONS } from '../lib/registrationForm';

/**
 * Two contact persons side by side, as the registration form has them:
 * Name (1) | Name (2), Department | Department, …
 */
function contactPair(a: number, b: number, section: string): FieldDef[] {
  const parts = [
    ['Name', 'Contact Person Name', 200], ['Dept', 'Department', 100], ['Designation', 'Designation', 100],
    ['Phone', 'Phone Number', 50], ['Email', 'Email Address', 200], ['AltMobile', 'Alt. Mobile / Extension', 50],
  ] as const;
  return parts.flatMap(([key, label, max]) => [a, b].map((n, i): FieldDef => ({
    name: `contact${n}${key}`, label: `${label} (${i + 1})`, max, section,
    type: key === 'Email' ? 'email' : undefined,
  })));
}

/** Vendor Master holds the vendor registration form; Print fills it in. */
export const VENDORS: SimpleMasterPageConfig = {
  apiBase: '/api/vendors', routeBase: '/vendors', singular: 'Vendor', plural: 'Vendors',
  editPerm: 'EDIT_VENDORS',
  printable: true,
  fields: [
    { name: 'name', label: 'Vendor / Company Name', required: true, list: true, max: 1600, section: REG_SECTIONS.general },
    { name: 'registrationId', label: 'Registration ID', max: 100, section: REG_SECTIONS.general },
    { name: 'legalName', label: 'Vendor Name (Legal Name)', max: 500, section: REG_SECTIONS.general },
    { name: 'businessType', label: 'Business Type', max: 100, suggestions: BUSINESS_TYPES, section: REG_SECTIONS.general },
    { name: 'mainContactName', label: 'Contact Person Name', list: true, max: 200, section: REG_SECTIONS.general },
    { name: 'mainContactDesignation', label: 'Designation', max: 100, section: REG_SECTIONS.general },
    { name: 'phone', label: 'Phone', type: 'number', list: true, section: REG_SECTIONS.general },
    { name: 'email', label: 'Email', type: 'email', list: true, max: 100, section: REG_SECTIONS.general },
    { name: 'fax', label: 'Fax', max: 50, section: REG_SECTIONS.general },

    { name: 'address', label: 'Business Address', type: 'textarea', wide: true, max: 5500, section: REG_SECTIONS.address },
    { name: 'city', label: 'City', max: 50, section: REG_SECTIONS.address },
    { name: 'zip', label: 'ZIP / Postal Code', max: 20, section: REG_SECTIONS.address },
    { name: 'state', label: 'State / Province', max: 50, section: REG_SECTIONS.address },
    { name: 'country', label: 'Country', max: 50, section: REG_SECTIONS.address },

    ...contactPair(1, 2, REG_SECTIONS.primary),
    ...contactPair(3, 4, REG_SECTIONS.secondary),

    { name: 'gstNo', label: 'GST No.', list: true, max: 50, section: REG_SECTIONS.tax },
    { name: 'panNo', label: 'PAN No.', max: 20, section: REG_SECTIONS.tax },
    { name: 'bankName', label: 'Bank Name', max: 200, section: REG_SECTIONS.tax },
    { name: 'bankBranch', label: 'Bank Branch', max: 200, section: REG_SECTIONS.tax },
    { name: 'bankAccountNo', label: 'Account Number', max: 50, section: REG_SECTIONS.tax },
    { name: 'bankIfsc', label: 'IFSC Code', max: 20, section: REG_SECTIONS.tax },
    { name: 'msmeNo', label: 'MSME', max: 100, placeholder: 'Udyam registration no. — or No', section: REG_SECTIONS.tax },
    { name: 'creditTerms', label: 'Credit Terms', max: 200, placeholder: 'e.g. 60 days', section: REG_SECTIONS.tax },
    { name: 'tin', label: 'TIN Number (old)', max: 50, section: REG_SECTIONS.tax },

    { name: 'description', label: 'Description', type: 'textarea', max: 2500, section: 'Other' },
    { name: 'assetId', label: 'Asset ID', max: 50, section: 'Other' },
  ],
};

export const MACHINES: SimpleMasterPageConfig = {
  apiBase: '/api/machines', routeBase: '/machines', singular: 'Machine', plural: 'Machines',
  fields: [
    { name: 'code', label: 'Code', list: true },
    { name: 'name', label: 'Name', required: true, list: true },
    // Codes from the Department and Vendor masters (D005, not "Pasting").
    { name: 'department', label: 'Department', list: true, type: 'lookup', lookupEndpoint: '/api/lookups/departments' },
    { name: 'capacity', label: 'Capacity', list: true },
    { name: 'color', label: 'Color' },
    { name: 'vendor', label: 'Vendor', type: 'lookup', lookupEndpoint: '/api/lookups/vendors' },
    { name: 'description', label: 'Description', type: 'textarea' },
    { name: 'forPlanner', label: 'Show in Planner', type: 'bool', list: true },
    { name: 'displayName', label: 'Planner Display Name', list: true },
  ],
};

export const SHEETS: SimpleMasterPageConfig = {
  apiBase: '/api/sheets', routeBase: '/sheets', singular: 'Sheet', plural: 'Sheets',
  fields: [
    { name: 'length', label: 'Length (mm)', required: true, list: true },
    { name: 'width', label: 'Width (mm)', required: true, list: true },
    { name: 'gsm', label: 'GSM', list: true },
    {
      name: 'paperTypeCode', label: 'Paper Type', list: true,
      type: 'lookup', lookupEndpoint: '/api/lookups/paper-types',
    },
  ],
};

export const PAPER_TYPES: SimpleMasterPageConfig = {
  apiBase: '/api/paper-types', routeBase: '/paper-types', singular: 'Paper Type', plural: 'Paper Types',
  fields: [
    { name: 'name', label: 'Name', required: true, list: true },
    { name: 'description', label: 'Description', type: 'textarea', list: true },
  ],
};

export const TOP_FINISHES: SimpleMasterPageConfig = {
  apiBase: '/api/top-finishes', routeBase: '/top-finishes', singular: 'Lamination Type', plural: 'Lamination Types',
  fields: [
    { name: 'name', label: 'Name', required: true, list: true },
  ],
};

export const HOLIDAYS: SimpleMasterPageConfig = {
  apiBase: '/api/holidays', routeBase: '/holidays', singular: 'Holiday', plural: 'Holidays',
  fields: [
    { name: 'name', label: 'Holiday Name', required: true, list: true },
    { name: 'date', label: 'Date', type: 'date', required: true, list: true },
  ],
};

export const NEWS: SimpleMasterPageConfig = {
  apiBase: '/api/news', routeBase: '/news', singular: 'News Item', plural: 'News & Updates',
  fields: [
    { name: 'title', label: 'Title', required: true, list: true },
    { name: 'description', label: 'Description', type: 'textarea' },
  ],
};

export const EMPLOYEES: SimpleMasterPageConfig = {
  apiBase: '/api/employees', routeBase: '/employees', singular: 'Employee', plural: 'Employees',
  fields: [
    { name: 'title', label: 'Title (Mr/Ms)' },
    { name: 'firstName', label: 'First Name', required: true, list: true },
    { name: 'middleName', label: 'Middle Name' },
    { name: 'lastName', label: 'Last Name', list: true },
    { name: 'username', label: 'Username', list: true },
    { name: 'password', label: 'Password' },
    { name: 'dob', label: 'Date of Birth (free text)' },
    { name: 'phone', label: 'Phone', list: true },
    { name: 'email', label: 'Email', type: 'email', list: true },
    // Codes from the Department and Designation masters.
    { name: 'department', label: 'Department', type: 'lookup', lookupEndpoint: '/api/lookups/departments' },
    { name: 'designation', label: 'Designation', list: true, type: 'lookup', lookupEndpoint: '/api/lookups/designations' },
    { name: 'reportingTo', label: 'Reports To' },
    { name: 'panNo', label: 'PAN' },
    { name: 'aadharNo', label: 'Aadhar' },
    { name: 'address', label: 'Address', type: 'textarea' },
    { name: 'country', label: 'Country' },
    { name: 'state', label: 'State' },
    { name: 'city', label: 'City' },
    { name: 'imagePath', label: 'Image Path' },
    { name: 'isAdmin', label: 'Is Admin', type: 'bool' },
  ],
};

export const DEPARTMENTS: SimpleMasterPageConfig = {
  apiBase: '/api/departments', routeBase: '/departments', singular: 'Department', plural: 'Departments',
  fields: [
    { name: 'name', label: 'Department Name', required: true, list: true },
    { name: 'alias', label: 'Alias', list: true },
    { name: 'description', label: 'Description', type: 'textarea' },
  ],
};

export const PANTONE_COLORS: SimpleMasterPageConfig = {
  apiBase: '/api/pantone-colors', routeBase: '/pantone-colors', singular: 'Pantone Color', plural: 'Pantone Colors',
  fields: [
    { name: 'name', label: 'Color Name', required: true, list: true },
  ],
};

export const DESIGNATIONS: SimpleMasterPageConfig = {
  apiBase: '/api/designations', routeBase: '/designations', singular: 'Designation', plural: 'Designations',
  fields: [
    { name: 'name', label: 'Designation Name', required: true, list: true },
    { name: 'description', label: 'Description', type: 'textarea', list: true },
  ],
};
